<script setup>
import { reactive, watch, computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { useMapGetter } from 'dashboard/composables/store';
import {
  buildTemplateParameters,
  findComponentByType,
  replaceTemplateVariables,
  COMPONENT_TYPES,
  MEDIA_FORMATS,
} from 'dashboard/helper/templateHelper';

import Button from 'dashboard/components-next/button/Button.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import Select from 'dashboard/components-next/select/Select.vue';
import Switch from 'dashboard/components-next/switch/Switch.vue';
import TextArea from 'dashboard/components-next/textarea/TextArea.vue';

const props = defineProps({
  assistant: {
    type: Object,
    default: () => ({}),
  },
});

const { t } = useI18n();

const MAX_STEPS = 5;
const DELAY_UNITS = ['minutes', 'hours', 'days'];

const whatsappInboxes = useMapGetter('inboxes/getWhatsAppInboxes');
const getTemplates = useMapGetter('inboxes/getFilteredWhatsAppTemplates');

const state = reactive({
  enabled: false,
  steps: [],
});

const templateKey = template => `${template.name}::${template.language}`;

// Only templates whose variables are plain body text can be filled here:
// media headers and button parameters need per-message values.
const isSupportedTemplate = template => {
  const header = findComponentByType(template, COMPONENT_TYPES.HEADER);
  if (MEDIA_FORMATS.includes(header?.format)) return false;
  return !buildTemplateParameters(template, false).buttons;
};

const availableTemplates = computed(() => {
  const unique = new Map();
  whatsappInboxes.value.forEach(inbox => {
    getTemplates.value(inbox.id).forEach(template => {
      if (isSupportedTemplate(template) && !unique.has(templateKey(template))) {
        unique.set(templateKey(template), template);
      }
    });
  });
  return [...unique.values()];
});

// A saved template stays selectable even before the inbox templates load
// (or after it stops being listed), so opening the page never drops it.
const templateOptions = computed(() => {
  const options = new Map();
  availableTemplates.value.forEach(template => {
    options.set(templateKey(template), template);
  });
  state.steps.forEach(step => {
    if (step.savedTemplate && !options.has(step.templateKey)) {
      options.set(step.templateKey, step.savedTemplate);
    }
  });

  return [
    { value: '', label: t('CAPTAIN.ASSISTANTS.FORM.FOLLOWUP.TEMPLATE_NONE') },
    ...[...options.entries()].map(([value, template]) => ({
      value,
      label: `${template.name} (${template.language})`,
    })),
  ];
});

const unitOptions = DELAY_UNITS.map(unit => ({
  value: unit,
  label: t(`CAPTAIN.ASSISTANTS.FORM.FOLLOWUP.UNITS.${unit}`),
}));

const findTemplate = key =>
  availableTemplates.value.find(template => templateKey(template) === key);

const buildStep = (step = {}) => ({
  delayValue: step.delay_value || 1,
  delayUnit: step.delay_unit || 'hours',
  description: step.description || '',
  templateKey: step.template?.name
    ? templateKey({
        name: step.template.name,
        language: step.template.language,
      })
    : '',
  body: { ...(step.template?.processed_params?.body || {}) },
  savedTemplate: step.template?.name ? step.template : null,
});

const addStep = () => {
  if (state.steps.length < MAX_STEPS) state.steps.push(buildStep());
};

const removeStep = index => {
  state.steps.splice(index, 1);
};

// Keeps the values already typed for variables that still exist in the new template.
const handleTemplateChange = (step, key) => {
  const template = findTemplate(key);
  const variables = template
    ? buildTemplateParameters(template, false).body || {}
    : {};
  const previous = step.body;
  step.templateKey = key;
  step.savedTemplate = null;
  step.body = Object.keys(variables).reduce((acc, variable) => {
    acc[variable] = previous[variable] || '';
    return acc;
  }, {});
};

const templatePreview = step => {
  const template = findTemplate(step.templateKey);
  const body = findComponentByType(template || {}, COMPONENT_TYPES.BODY);
  if (!body?.text) return '';
  return replaceTemplateVariables(body.text, { body: step.body });
};

const serializeTemplate = step => {
  const template = findTemplate(step.templateKey) || step.savedTemplate;
  if (!template || !step.templateKey) return undefined;

  return {
    name: template.name,
    language: template.language,
    category: template.category,
    namespace: template.namespace,
    processed_params: Object.keys(step.body).length ? { body: step.body } : {},
  };
};

// Returns this section's partial payload only — never spreads
// props.assistant.config, so the parent page can merge all sections'
// payloads together in one PATCH.
const getPayload = () => ({
  config: {
    followup_enabled: state.enabled,
    followup_steps: state.steps.map(step => {
      const template = serializeTemplate(step);
      return {
        description: step.description.trim(),
        delay_value: Math.max(Number(step.delayValue) || 1, 1),
        delay_unit: step.delayUnit,
        ...(template ? { template } : {}),
      };
    }),
  },
});

let initialPayloadSnapshot = JSON.stringify(getPayload());

const updateStateFromAssistant = assistant => {
  const { config = {} } = assistant;
  state.enabled = !!config.followup_enabled;
  state.steps = (config.followup_steps || []).map(buildStep);
  initialPayloadSnapshot = JSON.stringify(getPayload());
};

const isDirty = computed(
  () => JSON.stringify(getPayload()) !== initialPayloadSnapshot
);

defineExpose({ getPayload, isDirty });

watch(
  () => props.assistant,
  assistant => {
    if (assistant) updateStateFromAssistant(assistant);
  },
  { immediate: true }
);
</script>

<template>
  <div class="flex flex-col gap-4">
    <div class="flex items-center gap-3">
      <Switch v-model="state.enabled" />
      <span class="text-sm text-n-slate-12">
        {{ t('CAPTAIN.ASSISTANTS.FORM.FOLLOWUP.ENABLED_LABEL') }}
      </span>
    </div>

    <div v-if="state.enabled" class="flex flex-col gap-4">
      <div
        v-for="(step, index) in state.steps"
        :key="index"
        class="flex flex-col gap-3 p-4 rounded-lg bg-n-alpha-1"
      >
        <div class="flex items-center justify-between">
          <span class="text-sm font-medium text-n-slate-12">
            {{
              t('CAPTAIN.ASSISTANTS.FORM.FOLLOWUP.STEP', { number: index + 1 })
            }}
          </span>
          <Button
            icon="i-lucide-x"
            variant="ghost"
            color="slate"
            size="sm"
            @click="removeStep(index)"
          />
        </div>

        <div class="flex flex-wrap items-center gap-2">
          <span class="text-sm text-n-slate-11">
            {{ t('CAPTAIN.ASSISTANTS.FORM.FOLLOWUP.WAIT_LABEL') }}
          </span>
          <Input v-model="step.delayValue" type="number" min="1" class="w-24" />
          <Select
            v-model="step.delayUnit"
            :options="unitOptions"
            class="w-32"
          />
          <span class="text-sm text-n-slate-11">
            {{ t('CAPTAIN.ASSISTANTS.FORM.FOLLOWUP.WAIT_SUFFIX') }}
          </span>
        </div>

        <TextArea
          v-model="step.description"
          :label="t('CAPTAIN.ASSISTANTS.FORM.FOLLOWUP.GUIDANCE_LABEL')"
          :placeholder="
            t('CAPTAIN.ASSISTANTS.FORM.FOLLOWUP.GUIDANCE_PLACEHOLDER')
          "
          :max-length="300"
          auto-height
        />

        <div class="flex flex-col gap-2">
          <label class="text-sm font-medium text-n-slate-12">
            {{ t('CAPTAIN.ASSISTANTS.FORM.FOLLOWUP.TEMPLATE_LABEL') }}
          </label>
          <Select
            :model-value="step.templateKey"
            :options="templateOptions"
            :placeholder="
              t('CAPTAIN.ASSISTANTS.FORM.FOLLOWUP.TEMPLATE_PLACEHOLDER')
            "
            @update:model-value="handleTemplateChange(step, $event)"
          />
          <p class="text-xs text-n-slate-11">
            {{
              availableTemplates.length
                ? t('CAPTAIN.ASSISTANTS.FORM.FOLLOWUP.TEMPLATE_HINT')
                : t('CAPTAIN.ASSISTANTS.FORM.FOLLOWUP.NO_TEMPLATES')
            }}
          </p>

          <template v-if="step.templateKey">
            <Input
              v-for="variable in Object.keys(step.body)"
              :key="variable"
              v-model="step.body[variable]"
              :label="
                t('CAPTAIN.ASSISTANTS.FORM.FOLLOWUP.VARIABLE_LABEL', {
                  key: variable,
                })
              "
              :placeholder="
                t('CAPTAIN.ASSISTANTS.FORM.FOLLOWUP.VARIABLE_PLACEHOLDER')
              "
            />
            <div class="flex flex-col gap-1 p-3 rounded-lg bg-n-alpha-black2">
              <span class="text-xs font-medium text-n-slate-11">
                {{ t('CAPTAIN.ASSISTANTS.FORM.FOLLOWUP.PREVIEW') }}
              </span>
              <p class="text-sm whitespace-pre-wrap text-n-slate-12">
                {{ templatePreview(step) }}
              </p>
            </div>
          </template>
        </div>
      </div>

      <Button
        v-if="state.steps.length < MAX_STEPS"
        icon="i-lucide-plus"
        variant="ghost"
        size="sm"
        class="!w-fit !text-n-brand hover:!text-n-brand"
        :label="t('CAPTAIN.ASSISTANTS.FORM.FOLLOWUP.ADD_STEP')"
        @click="addStep"
      />

      <p class="text-xs italic text-n-slate-11">
        {{ t('CAPTAIN.ASSISTANTS.FORM.FOLLOWUP.FOOTNOTE') }}
      </p>
    </div>
  </div>
</template>
